import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../Configurations/Urls";

function api() {
  return {
    get: (url, config = {}) =>
      axios.get(`${MAIN_URL}${url}`, {
        ...config,
        headers: { ...getApiHeaders(), ...(config.headers || {}) },
      }),
    post: (url, data, config = {}) =>
      axios.post(`${MAIN_URL}${url}`, data, {
        ...config,
        headers: { ...getApiHeaders(), ...(config.headers || {}) },
      }),
    put: (url, data, config = {}) =>
      axios.put(`${MAIN_URL}${url}`, data, {
        ...config,
        headers: { ...getApiHeaders(), ...(config.headers || {}) },
      }),
    delete: (url, config = {}) =>
      axios.delete(`${MAIN_URL}${url}`, {
        ...config,
        headers: { ...getApiHeaders(), ...(config.headers || {}) },
      }),
  };
}

function unwrapError(error, fallback) {
  if (error.response?.status === 401) {
    throw new Error("Session Expired!");
  }
  const msg =
    error.response?.data?.error ||
    error.response?.data?.message ||
    error.response?.data?.details ||
    fallback;
  throw new Error(typeof msg === "string" ? msg : fallback);
}

export async function listApplicationUsers() {
  try {
    const res = await api().get("/api/application/user");
    return Array.isArray(res.data?.users) ? res.data.users : [];
  } catch (error) {
    unwrapError(error, "Failed to load users");
  }
}

export async function getApplicationUser(userId) {
  try {
    const res = await api().get(`/api/application/user/${userId}`);
    return res.data?.users || null;
  } catch (error) {
    unwrapError(error, "Failed to load user");
  }
}

export async function createApplicationUser(payload) {
  try {
    const res = await api().post("/api/application/user", payload);
    return res.data?.users;
  } catch (error) {
    if (error.response?.status === 422) {
      const errs = error.response.data?.errors;
      const first = errs && Object.values(errs).flat()?.[0];
      throw new Error(first || "Validation failed");
    }
    unwrapError(error, "Failed to create user");
  }
}

export async function updateApplicationUser(userId, payload) {
  try {
    const res = await api().put(`/api/application/user/${userId}`, payload);
    return res.data?.users;
  } catch (error) {
    if (error.response?.status === 422) {
      const errs = error.response.data?.errors;
      const first = errs && Object.values(errs).flat()?.[0];
      throw new Error(first || "Validation failed");
    }
    unwrapError(error, "Failed to update user");
  }
}

export async function deleteApplicationUser(userId) {
  try {
    await api().delete(`/api/application/user/${userId}`);
  } catch (error) {
    unwrapError(error, "Failed to delete user");
  }
}

export async function listApplicationRoles() {
  try {
    const res = await api().get("/api/application/userrole");
    const roles = res.data?.userroles ?? res.data?.roles ?? res.data;
    return Array.isArray(roles) ? roles : [];
  } catch (error) {
    unwrapError(error, "Failed to load roles");
  }
}

export async function getApplicationRole(roleId) {
  try {
    const res = await api().get(`/api/application/userrole/${roleId}`);
    return res.data?.userroles || res.data?.role || res.data;
  } catch (error) {
    unwrapError(error, "Failed to load role");
  }
}

export async function createApplicationRole(payload) {
  try {
    const res = await api().post("/api/application/userrole", payload);
    return res.data?.userroles || res.data;
  } catch (error) {
    if (error.response?.status === 422) {
      const errs = error.response.data?.errors;
      const first = errs && Object.values(errs).flat()?.[0];
      throw new Error(first || "Validation failed");
    }
    unwrapError(error, "Failed to create role");
  }
}

export async function updateApplicationRole(roleId, payload) {
  try {
    const res = await api().put(`/api/application/userrole/${roleId}`, payload);
    return res.data?.userroles || res.data;
  } catch (error) {
    unwrapError(error, "Failed to update role");
  }
}

export async function deleteApplicationRole(roleId) {
  try {
    await api().delete(`/api/application/userrole/${roleId}`);
  } catch (error) {
    unwrapError(error, "Failed to delete role");
  }
}

export async function listRoleAssignments() {
  try {
    const res = await api().get("/api/application/userrole-assignment");
    return Array.isArray(res.data?.roleAssignment)
      ? res.data.roleAssignment
      : [];
  } catch (error) {
    unwrapError(error, "Failed to load role assignments");
  }
}

export async function createRoleAssignment(payload) {
  try {
    const res = await api().post("/api/application/userrole-assignment", payload);
    return res.data?.roleAssignment;
  } catch (error) {
    unwrapError(error, "Failed to assign role");
  }
}

export async function updateRoleAssignment(assignmentId, payload) {
  try {
    const res = await api().put(
      `/api/application/userrole-assignment/${assignmentId}`,
      payload
    );
    return res.data?.roleAssignment;
  } catch (error) {
    unwrapError(error, "Failed to update role assignment");
  }
}

export async function deleteRoleAssignment(assignmentId) {
  try {
    await api().delete(`/api/application/userrole-assignment/${assignmentId}`);
  } catch (error) {
    unwrapError(error, "Failed to remove role assignment");
  }
}

export async function syncUserRoleAssignment(
  applicationUserId,
  applicationUserRoleId,
  existingAssignmentId = null
) {
  if (!applicationUserRoleId) {
    if (existingAssignmentId) {
      await deleteRoleAssignment(existingAssignmentId);
    }
    return null;
  }

  if (existingAssignmentId) {
    return updateRoleAssignment(existingAssignmentId, {
      application_user_id: applicationUserId,
      application_user_role_id: applicationUserRoleId,
      is_active: true,
    });
  }

  const all = await listRoleAssignments();
  const existing = (all || []).find(
    (a) => Number(a.application_user_id) === Number(applicationUserId)
  );
  if (existing?.application_user_role_assignment_id) {
    return updateRoleAssignment(existing.application_user_role_assignment_id, {
      application_user_id: applicationUserId,
      application_user_role_id: applicationUserRoleId,
      is_active: true,
    });
  }

  return createRoleAssignment({
    application_user_id: applicationUserId,
    application_user_role_id: applicationUserRoleId,
    is_active: true,
  });
}

export async function listApplicationRolePermissions() {
  try {
    const res = await api().get("/api/application/role-permissions");
    return Array.isArray(res.data?.permissions) ? res.data.permissions : [];
  } catch (error) {
    unwrapError(error, "Failed to load role permissions");
  }
}

export async function getApplicationRolePermissions(roleId) {
  try {
    const res = await api().get(
      `/api/application/role-permissions/particular/${roleId}`
    );
    return res.data;
  } catch (error) {
    unwrapError(error, "Failed to load role permissions");
  }
}

export async function saveApplicationRolePermissions(payload) {
  try {
    const res = await api().post("/api/application/role-permissions", payload);
    return res.data;
  } catch (error) {
    unwrapError(error, "Failed to save role permissions");
  }
}

export async function deleteApplicationRolePermissions(roleId) {
  try {
    await api().delete(
      `/api/application/role-permissions/particular/${roleId}`
    );
  } catch (error) {
    unwrapError(error, "Failed to delete role permissions");
  }
}

export async function listApplicationModules() {
  try {
    const res = await api().get("/api/application/module");
    const modules = res.data?.modules ?? res.data?.data ?? res.data;
    return Array.isArray(modules) ? modules : [];
  } catch (error) {
    unwrapError(error, "Failed to load modules");
  }
}

export async function listApplicationModuleActions() {
  try {
    const res = await api().get("/api/application/module-action");
    const actions =
      res.data?.moduleaction ??
      res.data?.module_actions ??
      res.data?.moduleActions ??
      res.data?.data ??
      res.data;
    return Array.isArray(actions) ? actions : [];
  } catch (error) {
    unwrapError(error, "Failed to load module actions");
  }
}
